import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, ArrowRight, ShieldCheck, PlayCircle, Zap, Star } from "lucide-react";
import InteractiveShowcase from "./InteractiveShowcase";

export default function Hero() {
  return (
    <section className="relative overflow-hidden pt-12 md:pt-20 pb-16 md:pb-24">
      {/* Subtle Background Glow Gradient */}
      <div
        className="absolute inset-0 -z-10 opacity-70 pointer-events-none"
        style={{
          background:
            "radial-gradient(55% 45% at 50% 8%, rgba(245, 158, 11, 0.08) 0%, rgba(250, 250, 250, 0) 70%)",
        }}
      />

      <div className="max-w-6xl mx-auto px-6 text-center">
        {/* Top Feature Pill */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="inline-flex items-center gap-2 text-xs font-semibold text-zinc-800 bg-zinc-100 hover:bg-zinc-200/80 border border-zinc-300/70 rounded-full px-4 py-1.5 shadow-sm transition-all"
        >
          <span className="flex h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
          <span className="text-zinc-600">Optics AI 2.4 Live in NZ & AU</span>
          <span className="text-zinc-300">|</span>
          <span className="text-amber-700 font-bold flex items-center gap-1">
            <Sparkles className="h-3 w-3" /> Demand AI for Cafés
          </span>
        </motion.div>

        {/* Hero Title */}
        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.08 }}
          className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-zinc-900 mt-6 max-w-4xl mx-auto leading-[1.12] tracking-tight"
        >
          Stop Guessing Café Demand.{" "}
          <span className="bg-gradient-to-r from-amber-600 via-amber-700 to-zinc-900 bg-clip-text text-transparent">
            Start Boosting Margins.
          </span>
        </motion.h1>

        {/* Hero Lede */}
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.16 }}
          className="text-base sm:text-lg md:text-xl text-zinc-600 mt-6 max-w-2xl mx-auto leading-relaxed"
        >
          Optics connects to your POS, factors in local weather forecasts, holidays, and events,
          and generates daily product-level forecasts — telling you{" "}
          <strong className="text-zinc-900 font-semibold">exactly what to prep and order</strong>.
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.24 }}
          className="mt-8 flex flex-wrap justify-center items-center gap-3.5"
        >
          <Link
            data-testid="cta-start"
            to="/login"
            className="inline-flex items-center gap-2 bg-zinc-900 hover:bg-zinc-800 text-white px-7 py-3.5 rounded-xl font-semibold text-sm transition-all duration-200 shadow-lg shadow-zinc-900/25 hover:shadow-xl hover:-translate-y-0.5"
          >
            Start Free 14-Day Trial <ArrowRight className="h-4 w-4" />
          </Link>

          <Link
            data-testid="cta-demo"
            to="/login"
            className="inline-flex items-center gap-2 bg-white hover:bg-zinc-50 border border-zinc-300 text-zinc-900 px-6 py-3.5 rounded-xl font-semibold text-sm transition-all duration-200 shadow-sm hover:border-zinc-400"
          >
            <PlayCircle className="h-4 w-4 text-amber-600" />
            Launch Live Demo Sandbox
          </Link>
        </motion.div>

        {/* Trust Badges */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.32 }}
          className="mt-6 flex flex-wrap items-center justify-center gap-6 text-xs text-zinc-500 font-medium"
        >
          <span className="flex items-center gap-1.5">
            <ShieldCheck className="h-4 w-4 text-emerald-600" /> 14-day free trial · card required · cancel anytime
          </span>
          <span className="flex items-center gap-1.5">
            <Zap className="h-4 w-4 text-amber-600" /> 3-minute POS setup
          </span>
          <span className="flex items-center gap-1.5">
            <Star className="h-4 w-4 fill-amber-400 text-amber-400" /> Daily product-level forecasts
          </span>
        </motion.div>

        {/* Interactive Live Showcase Hero Widget */}
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.38 }}
          className="mt-14 max-w-5xl mx-auto"
        >
          <div className="relative">
            <div className="absolute -inset-1.5 bg-gradient-to-r from-amber-500/20 via-zinc-500/10 to-sky-500/20 rounded-3xl blur-lg -z-10 opacity-75" />
            <InteractiveShowcase />
          </div>
        </motion.div>
      </div>
    </section>
  );
}
